import { BCMSDefaultModel, BCMSRecord, Workflow, useBCMSContext } from "@bcm-dev/bcms-vite";
import CameraViewPage from "components/pages/CameraViewPage";
import CheckImageBackConfirmPage from "components/pages/CheckImageBackConfirmPage";
import CheckImageBackConfirmManualPage from "components/pages/CheckImageBackConfirmManualPage/CheckImageBackConfirmManualPage";
import CheckImageBackPage from "components/pages/CheckImageBackPage";
import CheckImageBackManualPage from "components/pages/CheckImageBackManualPage";
import CheckImageConfirmPage from "components/pages/CheckImageConfirmPage";
import CheckImageConfirmManualPage from "components/pages/CheckImageConfirmManualPage/CheckImageConfirmManualPage";
import CheckImageGuidelinesPage from "components/pages/CheckImageGuidelinesPage";
import CheckImageGuidelinesManualPage from "components/pages/CheckImageGuidelinesManualPage";
import CheckImagePage from "components/pages/CheckImagePage";
import CheckImageManualPage from "components/pages/CheckImageManualPage";
import DepositACheckConfirmationPage from "components/pages/DepositACheckConfirmationPage";
import DepositACheckPage from "components/pages/DepositACheckPage";
import { Account, CODAccount, CheckingAccount, Deposit, LoanAccount, MobileDevice, SavingsAccount } from "model";
import React from "react";

// Steps of the workflow
const steps = [ 
  DepositACheckPage, // 0 
  CameraViewPage, // 1
  DepositACheckConfirmationPage, // 2

  // START: Different workflow/scenario within the workflow.
  CheckImageGuidelinesPage, // 3
  CheckImagePage, // 4
  CheckImageConfirmPage, // 5
  CheckImageBackPage, // 6
  CheckImageBackConfirmPage, // 7
  // END: Different workflow/scenario within the workflow.

  // START: Workflow for "Manual Front & Back Images".
  CheckImageGuidelinesManualPage, // 8
  CheckImageManualPage, // 9
  CheckImageConfirmManualPage, // 10
  CheckImageBackManualPage, // 11
  CheckImageBackConfirmManualPage, // 12
  // END: Workflow for "Manual Front & Back Images".
]; 

/**
 * @function DepositACheckWorkflow
 * @param {Void} - No arguments are passed.
 * @returns {React.Component}
 * @exported true
 * @description The workflow component for the "Deposit A Check" page.
 * 
 * Setting constructor example for state captures:
 * 
 * document.dispatchEvent(new CustomEvent("force-workflow-state", {
  detail: { 
    value: { 
      deposit: { id: 0, account: 2, amount: "123.00" },
      didSetFrontCheckView : true,
      didSetBackCheckView: true,
      selectedCheckView: "back"
    } 
  } 
}));
 */
export default function DepositACheckWorkflow(props) {
    const controller = useBCMSContext();
    // Get the inital state of the workflow
    const value = getDefault(controller);
    // Define the constructor function for forcing state through the 'force-workflow-state' event
    const constructor = record => {
        // Get the properties passed from a capture state.
        const recordDeposit = record.deposit ?? {};
        const recordAccounts = record.accounts ?? value.accounts;
        const depositFromObj = Deposit.init({ ...recordDeposit });
        let defaultDeposit = BCMSDefaultModel.deposit();
        let depositMergeObj = BCMSDefaultModel.mergeRecords(depositFromObj.record, defaultDeposit.record);
        let depositMerge = Deposit.init({...depositMergeObj});
        depositMerge.id = depositMerge.id === undefined ? Deposit.nextID() : depositMerge.id;

        const accounts = recordAccounts.reduce((acc, account) => {
          let existingAccount = Account.initByID(account.id);
          let modifiedAccount = Account.init({...existingAccount.record, ...account});
          acc.push(modifiedAccount);
          return acc;
      }, []);

        // Convert the records to classes.
        const depositProp = record.deposit ? { deposit: depositMerge } : {};
        const accountsProp = accounts.length > 0 ? { accounts: accounts } : {};

        // Return the new workflow default object.
        return { 
            ...getDefault(controller),
            ...record,
            ...depositProp,
            ...accountsProp
        };
    };

    // Return the workflow component
    return <Workflow value={ value } steps={ steps } constructor={ constructor } />
}
// Return the steps of the workflow
export const getWorkflowSteps = () => steps;

/**
 * @function getDefault
 * @param {Controller} controller - A global controller.
 * @returns {Object}
 * @exported true
 * @description Function to get the default state of the workflow
 */
export const getDefault = controller => {
    BCMSRecord.globalControllerFor([ Account, Deposit, MobileDevice, CheckingAccount, SavingsAccount, CODAccount, LoanAccount ], controller);
    const defaultDeposit = BCMSDefaultModel.deposit();

    defaultDeposit.account = undefined;
    defaultDeposit.amount.amount = undefined;
    
    // Return the default workflow state
    return {
        ui: {
          accountDropdown: { isOpen: false },
          checkFrontView: { isSet: false },
          checkBackView: { isSet: false },
          selectedCheckView: "front" // either "front" or "back".
        },
        data: {
          deposit: defaultDeposit,
        }
    };
};